import type { OrganizationId, OrganizationIdInput } from '@/lib/organization-scoping'
import queryClient from '@/lib/queryClient'
import { salesOrderQueries } from '@/lib/services/sales-orders/sales-orders.query'
import { BetaFeatureKey, hasBetaFeature } from '@/lib/beta-features'
import { PaymentType } from '@/lib/enums/PaymentType'
import { organizationQueries } from '@/lib/services/organizations/organizations.query'
import type { Organization } from '@/lib/services/organizations/organizations.types'
import type { SalesOrderIdInput } from '@/lib/services/sales-orders/sales-orders.types'
import { snapdragonQueries } from '@/lib/services/snapdragon/snapdragon.query'
import type {
  SalesOrderPrintTemplate,
  SnapdragonReceiptOutputSettings,
  SnapdragonReceiptPaperWidth,
  SnapdragonReceiptProtocol,
  SnapdragonSuperTicketOutputSettings,
  SnapdragonWorkstationResponse,
} from '@/lib/services/snapdragon/snapdragon.types'
import type { SuperTicketLayoutInput } from '@/lib/services/super-ticket-settings/super-ticket-settings.types'
import { getSuperTicketPrintPreferences } from '@/lib/super-ticket-print-preferences'
import {
  createLocalPrintProfileRevisionGuard,
  createLocalPrintProfileWriteGuard,
  loadLocalPrintProfile,
  saveLocalPrintPairing,
  syncLocalPrintWorkstation,
  type LocalPrintProfile,
} from '@/lib/local-print-profile'
import {
  getLocalPrintHealth,
  getLocalPrintStation,
  LocalPrintError,
  localPrintProtocolVersion,
  runLocalPrintIntent,
  type LocalPrintJob,
  type LocalPrintPrinter,
  type LocalPrintStation,
} from '@/lib/local-print-bridge'
import { paidReceiptPayments } from '@/lib/local-print-receipt-layout'
import {
  receiptDocumentLabel,
  renderReceiptRaster,
  renderReceiptTestRaster,
  type ReceiptRasterOrder,
} from '@/lib/local-print-receipt-renderer'
import { readProperty } from '@/lib/utils'

export * from '@/lib/local-print-profile'
export * from '@/lib/local-print-bridge'
export { renderReceiptRaster, renderReceiptTestRaster } from '@/lib/local-print-receipt-renderer'

/** The direct printing outputs that print a sales order document. */
export type LocalPrintDocumentKind = 'orderSummary' | 'superTicket'

/** A print's settings for an Order Summary or Super Ticket, over the computer's saved ones. */
export type LocalPrintDocumentOutputOverride = Partial<SnapdragonSuperTicketOutputSettings> & {
  layout?: SuperTicketLayoutInput
}

/** The profile fields a receipt print reads; POS auto-prints overlay a job's saved settings. */
export interface LocalPrintReceiptProfile {
  token: string
  receipt: {
    printerId: string | null
    protocol: SnapdragonReceiptProtocol
    paperWidthMm: SnapdragonReceiptPaperWidth
    cut: boolean
  }
}

/** The profile fields an Order Summary or Super Ticket test print reads. */
export type LocalPrintDocumentProfile = Pick<
  LocalPrintProfile,
  'token' | 'orderSummary' | 'superTicket'
>

const localReceiptProtocols = new Set(['escpos', 'starprnt', 'stargraphic'])
const legacyLocalReceiptProtocols = ['escpos', 'starprnt']

function isLocalReceiptProtocol(protocol: string): protocol is SnapdragonReceiptProtocol {
  return localReceiptProtocols.has(protocol)
}

export function detectedLocalReceiptProtocol(
  printer: Pick<LocalPrintPrinter, 'displayName' | 'name' | 'receiptProtocol'> | null | undefined
): SnapdragonReceiptProtocol | null {
  const protocol = String(printer?.receiptProtocol ?? '').toLowerCase()
  // Star Line is the requested default for detected Star printers, including Graphic reports.
  if (protocol === 'stargraphic') return 'starprnt'
  if (isLocalReceiptProtocol(protocol)) return protocol

  // Keep explicit ESC/POS reports above authoritative for switchable Star printers.
  const isStarPrinter = [printer?.displayName, printer?.name].some((name) =>
    /(?:^|[\s_-])star(?=$|[\s_-])/i.test(String(name ?? ''))
  )
  return isStarPrinter ? 'starprnt' : null
}

export function supportedLocalReceiptProtocols(
  station: Pick<LocalPrintStation, 'supportedReceiptProtocols'> | null | undefined
): Set<string> {
  const protocols = Array.isArray(station?.supportedReceiptProtocols)
    ? station.supportedReceiptProtocols
    : legacyLocalReceiptProtocols

  return new Set(
    protocols
      .map((protocol) => String(protocol).toLowerCase())
      .filter((protocol) => localReceiptProtocols.has(protocol))
  )
}

async function requireSupportedLocalReceiptProtocol(profile: LocalPrintReceiptProfile) {
  const protocol = profile.receipt.protocol
  if (protocol !== 'stargraphic') return

  let station
  try {
    station = await getLocalPrintStation(profile.token, { timeoutMs: 3_000 })
  } catch (cause) {
    throw new LocalPrintError(
      'OneStem could not confirm that Snapdragon supports Star Graphic. Try again.',
      { code: 'job_not_submitted', cause }
    )
  }
  if (supportedLocalReceiptProtocols(station).has(protocol)) return

  throw new LocalPrintError(
    'Star Graphic is not available in this version of Snapdragon. Open Direct Printing settings and choose another printer mode.',
    { code: 'receipt_protocol_unsupported' }
  )
}

async function currentLocalPrintOrganization(
  organizationId: OrganizationIdInput
): Promise<Organization> {
  const organizationQuery = organizationQueries.show(organizationId)

  try {
    await queryClient.invalidateQueries({
      queryKey: organizationQuery.queryKey,
      exact: true,
      refetchType: 'none',
    })
    const organization = await queryClient.fetchQuery(organizationQuery)
    if (!hasBetaFeature(organization, BetaFeatureKey.Snapdragon)) {
      throw new LocalPrintError('Direct Printing is not available for this shop.', {
        code: 'local_print_access_disabled',
      })
    }
    return organization
  } catch (cause) {
    if (readProperty(cause, 'code') === 'local_print_access_disabled') throw cause
    throw new LocalPrintError(
      'OneStem could not confirm Direct Printing access. Check the connection and try again.',
      { code: 'local_print_access_unavailable', cause }
    )
  }
}

export async function refreshLocalPrintWorkstationProfile(
  organizationId: OrganizationIdInput
): Promise<(SnapdragonWorkstationResponse & { profile: LocalPrintProfile }) | null> {
  // Workstations belong to an organization, so there's nothing to refresh without one.
  if (organizationId === null || organizationId === undefined) return null
  const writeIsCurrent = createLocalPrintProfileWriteGuard(organizationId)
  const profileRevisionIsCurrent = createLocalPrintProfileRevisionGuard(organizationId)
  const activeProfile = loadLocalPrintProfile(organizationId)
  if (!activeProfile.token) return null
  // Strip preferences left by older clients while retaining their local pairing.
  saveLocalPrintPairing(organizationId, activeProfile, { advanceRevision: false })

  const health = await getLocalPrintHealth({ timeoutMs: 3_000 })
  if (health.protocolVersion !== localPrintProtocolVersion || !health.computerId) return null

  const stored = loadLocalPrintProfile(organizationId, health.computerId)
  if (!stored.token) return null

  const station = await getLocalPrintStation(stored.token, { timeoutMs: 3_000 })
  if (!station.connected) return null

  const profile = {
    ...stored,
    computerId: health.computerId,
    machineId: health.machineId ?? '',
    stationId: station.stationId ?? '',
    stationName: station.stationName ?? '',
  }

  const workstation = await syncLocalPrintWorkstation({
    organizationId,
    profile,
    station,
    shouldPersist: () => writeIsCurrent() && profileRevisionIsCurrent(),
  })
  return writeIsCurrent() && profileRevisionIsCurrent() ? workstation : null
}

export async function printLocalSalesOrder({
  organizationId,
  salesOrderId,
  kind,
  reprint = false,
  source = 'manual',
  template: selectedTemplate,
  paperHasPreprintedBranding: selectedPaperHasPreprintedBranding,
  clientJobId,
  outputOverride,
  layout,
}: {
  organizationId: OrganizationId
  salesOrderId: SalesOrderIdInput
  kind: LocalPrintDocumentKind
  reprint?: boolean
  source?: 'manual' | 'auto'
  template?: SalesOrderPrintTemplate | null
  paperHasPreprintedBranding?: boolean | null
  clientJobId?: string
  outputOverride?: LocalPrintDocumentOutputOverride
  layout?: SuperTicketLayoutInput
}): Promise<LocalPrintJob> {
  const profile = loadLocalPrintProfile(organizationId)
  if (!profile.token) throw new Error('Pair this browser with Snapdragon first.')
  const storedOutput = kind === 'superTicket' ? profile.superTicket : profile.orderSummary
  const outputProfile = { ...storedOutput, ...outputOverride }
  if (outputProfile.enabled === false) {
    throw new Error(
      `Local ${kind === 'superTicket' ? 'super ticket' : 'order summary'} printing is turned off.`
    )
  }
  const { printerId } = outputProfile
  if (!printerId)
    throw new Error(
      `Choose a Direct Printing ${kind === 'superTicket' ? 'super ticket' : 'order summary'} printer first.`
    )
  const intentKey =
    clientJobId || `${organizationId}:${salesOrderId}:${kind}:${reprint ? 'reprint' : source}`
  return runLocalPrintIntent({
    organizationId,
    token: profile.token,
    intentKey,
    clientJobId,
    buildRequest: async (nextClientJobId) => {
      await currentLocalPrintOrganization(organizationId)
      const superTicketPreferences =
        kind === 'superTicket'
          ? getSuperTicketPrintPreferences({
              fallbackTemplate: profile.superTicket.template,
              fallbackPaperHasPreprintedBranding: profile.superTicket.paperHasPreprintedBranding,
            })
          : null
      // Preferences are read only for a Super Ticket.
      const template = superTicketPreferences
        ? selectedTemplate || outputOverride?.template || superTicketPreferences.template
        : 'order_summary'
      const paperHasPreprintedBranding =
        typeof selectedPaperHasPreprintedBranding === 'boolean'
          ? selectedPaperHasPreprintedBranding
          : (outputOverride?.paperHasPreprintedBranding ??
            superTicketPreferences?.paperHasPreprintedBranding)
      const bundle = await queryClient.fetchQuery(
        salesOrderQueries.localPrintBundle({
          organizationId,
          salesOrderId,
          template,
          ...(kind === 'superTicket'
            ? { paperHasPreprintedBranding, layout: layout ?? outputOverride?.layout }
            : {}),
        })
      )
      return {
        version: localPrintProtocolVersion,
        organizationId: String(organizationId),
        clientJobId: nextClientJobId,
        document: {
          ...bundle.document,
          title: `${reprint ? 'Reprint - ' : ''}${bundle.document.title}`,
        },
        output: {
          mode: 'windows_pdf',
          printerId,
          copies: Number(outputProfile.copies || 1),
          tray: outputProfile.tray || null,
          paperSize: bundle.page.paper_size,
          orientation: bundle.page.orientation,
          cut: false,
        },
      }
    },
  })
}

const legalLocalTestTemplates = new Set([
  'jhc_fb7373_super_ticket',
  'jhc_mercury_x_order_entry_super_ticket',
])

const landscapeLocalTestTemplates = new Set([
  'jhc_fb7373_super_ticket',
  'floranext_letter_super_ticket',
  'enclosure_card_only_letter',
])

function localDocumentTestSource(kind: LocalPrintDocumentKind, template: SalesOrderPrintTemplate) {
  const isSuperTicket = kind === 'superTicket'
  const title = isSuperTicket ? 'Super Ticket' : 'Order Summary'
  const paperSize = isSuperTicket && legalLocalTestTemplates.has(template) ? 'Legal' : 'Letter'
  const orientation =
    isSuperTicket && landscapeLocalTestTemplates.has(template) ? 'landscape' : 'portrait'

  return {
    title: `${title} test page`,
    paperSize,
    orientation,
    html: `<main><p class="eyebrow">Snapdragon Direct Printing</p><h1>${title}</h1><p>This test confirms that this computer can print to the selected printer and tray.</p><div class="rule"></div><p class="detail">Paper: ${paperSize} · ${orientation}</p></main>`,
    css: `@page { size: ${paperSize} ${orientation}; margin: 0.6in; }
      * { box-sizing: border-box; }
      body { margin: 0; color: #111; font-family: Arial, sans-serif; }
      main { border: 2px solid #222; padding: 0.45in; }
      .eyebrow { margin: 0 0 0.15in; font-size: 11pt; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; }
      h1 { margin: 0 0 0.2in; font-size: 24pt; }
      p { margin: 0; font-size: 12pt; line-height: 1.5; }
      .rule { margin: 0.35in 0 0.2in; border-top: 1px solid #555; }
      .detail { font-size: 10pt; }`,
  }
}

export async function printLocalDocumentTest({
  organizationId,
  kind,
  profileOverride,
}: {
  organizationId: OrganizationId
  kind: LocalPrintDocumentKind
  profileOverride?: LocalPrintDocumentProfile
}): Promise<LocalPrintJob> {
  const profile = profileOverride || loadLocalPrintProfile(organizationId)
  if (!profile.token) throw new Error('Pair this browser with Snapdragon first.')
  const outputProfile = kind === 'superTicket' ? profile.superTicket : profile.orderSummary
  const label = kind === 'superTicket' ? 'super ticket' : 'order summary'
  if (outputProfile.enabled === false) {
    throw new Error(`Local ${label} printing is turned off.`)
  }
  const { printerId } = outputProfile
  if (!printerId) {
    throw new Error(`Choose a Direct Printing ${label} printer first.`)
  }
  const intentKey = `${organizationId}:${kind}:test`
  return runLocalPrintIntent({
    organizationId,
    token: profile.token,
    intentKey,
    buildRequest: async (clientJobId) => {
      await currentLocalPrintOrganization(organizationId)
      const superTicketPreferences = getSuperTicketPrintPreferences({
        fallbackTemplate: profile.superTicket.template,
        fallbackPaperHasPreprintedBranding: profile.superTicket.paperHasPreprintedBranding,
      })
      const { paperSize, orientation, ...source } = localDocumentTestSource(
        kind,
        superTicketPreferences.template
      )
      return {
        version: localPrintProtocolVersion,
        organizationId: String(organizationId),
        clientJobId,
        document: { kind: 'html', ...source },
        output: {
          mode: 'windows_pdf',
          printerId,
          copies: 1,
          tray: outputProfile.tray || null,
          paperSize,
          orientation,
          cut: false,
        },
      }
    },
  })
}

async function salesOrderWithReceiptBranding(
  organizationId: OrganizationId,
  salesOrder: ReceiptRasterOrder
): Promise<ReceiptRasterOrder> {
  const organization = await currentLocalPrintOrganization(organizationId)
  return {
    ...salesOrder,
    organization: { ...salesOrder?.organization, ...organization },
  }
}

export async function printLocalReceipt({
  organizationId,
  salesOrder,
  salesOrderId = salesOrder?.id,
  reprint = false,
  openDrawer = false,
  openDrawerFromPayments = false,
  clientJobId,
  profileOverride,
}: {
  organizationId: OrganizationId
  salesOrder?: ReceiptRasterOrder
  salesOrderId?: SalesOrderIdInput
  reprint?: boolean
  openDrawer?: boolean
  openDrawerFromPayments?: boolean
  clientJobId?: string
  profileOverride?: LocalPrintReceiptProfile
}): Promise<LocalPrintJob> {
  const profile = profileOverride || loadLocalPrintProfile(organizationId)
  const receipt = profile.receipt
  const { printerId } = receipt
  if (!profile.token || !printerId) throw new Error('Local receipt printing is not configured.')
  const intentKey =
    clientJobId || `${organizationId}:${salesOrderId}:receipt:${reprint ? 'reprint' : 'manual'}`
  return runLocalPrintIntent({
    organizationId,
    token: profile.token,
    intentKey,
    clientJobId,
    buildRequest: async (nextClientJobId) => {
      const sourceSalesOrder =
        salesOrder ||
        (await queryClient.fetchQuery({
          ...salesOrderQueries.show({ organizationId, salesOrderId }),
          staleTime: 0,
        }))
      const brandedSalesOrder = await salesOrderWithReceiptBranding(
        organizationId,
        sourceSalesOrder
      )
      await requireSupportedLocalReceiptProtocol(profile)
      const raster = await renderReceiptRaster(brandedSalesOrder, {
        paperWidthMm: receipt.paperWidthMm,
        reprint,
      })
      const shouldOpenDrawer = openDrawerFromPayments
        ? paidReceiptPayments(brandedSalesOrder).some(
            ({ type }) => type === PaymentType.Cash.Value || type === PaymentType.Check.Value
          )
        : openDrawer
      return {
        version: localPrintProtocolVersion,
        organizationId: String(organizationId),
        clientJobId: nextClientJobId,
        document: {
          kind: 'raster',
          title: `${reprint ? 'Reprint - ' : ''}${receiptDocumentLabel(brandedSalesOrder)} ${brandedSalesOrder.reference_id || brandedSalesOrder.id}`,
          ...raster,
        },
        output: {
          mode: receipt.protocol,
          printerId,
          copies: 1,
          paperWidthMm: Number(receipt.paperWidthMm),
          cut: receipt.cut !== false,
          openDrawer: shouldOpenDrawer,
          tray: null,
        },
      }
    },
  })
}

export async function printLocalReceiptTest({
  organizationId,
  profileOverride,
}: {
  organizationId: OrganizationId
  profileOverride?: LocalPrintReceiptProfile
}): Promise<LocalPrintJob> {
  const profile = profileOverride || loadLocalPrintProfile(organizationId)
  const receipt = profile.receipt
  const { printerId } = receipt
  if (!profile.token || !printerId) throw new Error('Local receipt printing is not configured.')

  return runLocalPrintIntent({
    organizationId,
    token: profile.token,
    intentKey: `${organizationId}:test:receipt:manual`,
    buildRequest: async (clientJobId) => {
      await currentLocalPrintOrganization(organizationId)
      await requireSupportedLocalReceiptProtocol(profile)
      return {
        version: localPrintProtocolVersion,
        organizationId: String(organizationId),
        clientJobId,
        document: {
          kind: 'raster',
          title: 'Receipt test',
          ...renderReceiptTestRaster(),
        },
        output: {
          mode: receipt.protocol,
          printerId,
          copies: 1,
          paperWidthMm: Number(receipt.paperWidthMm),
          cut: receipt.cut !== false,
          openDrawer: false,
          tray: null,
        },
      }
    },
  })
}

export async function autoPrintCompletedPosReceipt({
  organizationId,
  salesOrderId,
  clientJobId,
  outputOverride,
}: {
  organizationId: OrganizationId
  salesOrderId: SalesOrderIdInput
  clientJobId?: string
  outputOverride?: Partial<SnapdragonReceiptOutputSettings>
}): Promise<LocalPrintJob | null> {
  const profile = loadLocalPrintProfile(organizationId)
  const receiptProfile = { ...profile, receipt: { ...profile.receipt, ...outputOverride } }
  const receipt = receiptProfile.receipt
  if (!profile.token || !receipt.autoPrint || !receipt.printerId) {
    return null
  }
  const printReceipt = (profileOverride: LocalPrintReceiptProfile) =>
    printLocalReceipt({
      organizationId,
      salesOrderId,
      openDrawerFromPayments: true,
      clientJobId: clientJobId || `${organizationId}:${salesOrderId}:receipt:auto`,
      profileOverride,
    })

  try {
    return await printReceipt(receiptProfile)
  } catch (error) {
    const code = readProperty(error, 'code')
    if (code === 'local_print_access_disabled') return null
    if (code !== 'receipt_protocol_unsupported') throw error

    let workstationResponse: SnapdragonWorkstationResponse
    try {
      // Recovery can run before the header has loaded this computer's saved preferences.
      workstationResponse = await queryClient.fetchQuery({
        ...snapdragonQueries.workstation({
          organizationId,
          computerId: profile.computerId,
          machineId: profile.machineId || undefined,
        }),
        staleTime: 0,
      })
    } catch (cause) {
      throw new LocalPrintError('Could not load saved printer settings. Try again.', {
        code: 'job_not_submitted',
        cause,
      })
    }
    const savedReceipt = workstationResponse?.workstation?.printing_settings?.receipt
    const savedProtocol = savedReceipt?.protocol
    const canUseCorrectedProtocol =
      savedReceipt &&
      String(savedReceipt.printerId) === String(receipt.printerId) &&
      savedProtocol !== undefined &&
      savedProtocol !== 'stargraphic' &&
      localReceiptProtocols.has(savedProtocol)
    if (!canUseCorrectedProtocol) throw error

    try {
      return await printReceipt({
        ...receiptProfile,
        receipt: { ...receipt, protocol: savedProtocol },
      })
    } catch (retryError) {
      if (readProperty(retryError, 'code') === 'local_print_access_disabled') return null
      throw retryError
    }
  }
}
